// ============================================
// 上岸小搭子 - 路由配置
// ============================================

import React, { Suspense } from 'react';
import { createBrowserRouter, Outlet, useLocation } from 'react-router-dom';
import MainLayout from '../components/Layout/MainLayout';

// 懒加载页面组件
const Login = React.lazy(() => import('../pages/Login'));
const UserAgreement = React.lazy(() => import('../pages/Agreement/UserAgreement'));
const PrivacyPolicy = React.lazy(() => import('../pages/Agreement/PrivacyPolicy'));
const Today = React.lazy(() => import('../pages/Today'));
const Practice = React.lazy(() => import('../pages/Practice'));
const AnswerSheet = React.lazy(() => import('../pages/Practice/AnswerSheet'));
const PracticeResult = React.lazy(() => import('../pages/Practice/PracticeResult'));
const WrongQuestionAnalysis = React.lazy(() => import('../pages/Practice/WrongQuestionAnalysis'));
const AIChat = React.lazy(() => import('../pages/AIChat'));
const CourseLive = React.lazy(() => import('../pages/Course/Live'));
const CourseVideo = React.lazy(() => import('../pages/Course/Video'));
const CourseTeacher = React.lazy(() => import('../pages/Course/Teacher'));
const LiveDetail = React.lazy(() => import('../pages/Course/LiveDetail'));
const VideoDetail = React.lazy(() => import('../pages/Course/VideoDetail'));
const TeacherDetail = React.lazy(() => import('../pages/Course/TeacherDetail'));
const Profile = React.lazy(() => import('../pages/Profile'));
const HistoryExam = React.lazy(() => import('../pages/PracticeMode/HistoryExam'));
const SmartPaper = React.lazy(() => import('../pages/PracticeMode/SmartPaper'));
const TimeLimit = React.lazy(() => import('../pages/PracticeMode/TimeLimit'));
const MockExam = React.lazy(() => import('../pages/PracticeMode/MockExam'));

// AI工具箱
const AIDiagnose = React.lazy(() => import('../pages/AITools/AIDiagnose'));
const EssayCorrection = React.lazy(() => import('../pages/AITools/EssayCorrection'));
const InterviewSim = React.lazy(() => import('../pages/AITools/InterviewSim'));
const PhotoQuestion = React.lazy(() => import('../pages/AITools/PhotoQuestion'));

// 直播
const TeacherLive = React.lazy(() => import('../pages/TeacherLive/TeacherLive'));

// 个人中心二级页面
const MyCourses = React.lazy(() => import('../pages/Profile/MyCourses'));
const WrongQuestions = React.lazy(() => import('../pages/Profile/WrongQuestions'));
const WrongAnalysis = React.lazy(() => import('../pages/Profile/WrongAnalysis'));
const StudyReport = React.lazy(() => import('../pages/Profile/StudyReport'));
const MyFavorites = React.lazy(() => import('../pages/Profile/MyFavorites'));
const StudyNotes = React.lazy(() => import('../pages/Profile/StudyNotes'));
const Settings = React.lazy(() => import('../pages/Profile/Settings'));
const EditProfile = React.lazy(() => import('../pages/Profile/EditProfile'));
const ChangePassword = React.lazy(() => import('../pages/Profile/ChangePassword'));
const BindPhone = React.lazy(() => import('../pages/Profile/BindPhone'));
const ClearCache = React.lazy(() => import('../pages/Profile/ClearCache'));
const VipRecharge = React.lazy(() => import('../pages/Profile/VipRecharge'));

// 加载中组件
const PageLoading = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    background: '#FFF8E7'
  }}>
    <div style={{ color: '#FF8C00', fontSize: '16px' }}>加载中...</div>
  </div>
);

// 带TabBar的布局
const MainLayoutWrapper = () => {
  const location = useLocation();
  
  // 判断是否为二级页面（需要隐藏TabBar）
  const isSubPage = 
    location.pathname.includes('/practice/answer-sheet') ||
    location.pathname.includes('/practice/result') ||
    location.pathname.includes('/practice/wrong-analysis') ||
    location.pathname.includes('/practice/history') ||
    location.pathname.includes('/practice/smart') ||
    location.pathname.includes('/practice/timelimit') ||
    location.pathname.includes('/practice/mock') ||
    location.pathname.includes('/course/live/') ||
    location.pathname.includes('/course/video/') ||
    location.pathname.includes('/course/teacher/') ||
    location.pathname.includes('/ai-tools/') ||
    location.pathname.includes('/teacher/live') ||
    location.pathname.includes('/profile/');

  return (
    <Suspense fallback={<PageLoading />}>
      {isSubPage ? <Outlet /> : (
        <MainLayout />
      )}
    </Suspense>
  );
};

// 路由配置
const router = createBrowserRouter([
  {
    path: '/',
    element: <Login />,
  },
  {
    path: '/',
    element: <MainLayoutWrapper />,
    children: [
      // 一级页面
      {
        path: 'today',
        element: <Today />,
      },
      {
        path: 'practice',
        element: <Practice />,
      },
      {
        path: 'ai-chat',
        element: <AIChat />,
      },
      // 课程页面
      {
        path: 'course',
        element: <CourseLive />,
      },
      {
        path: 'course/live',
        element: <CourseLive />,
      },
      {
        path: 'course/video',
        element: <CourseVideo />,
      },
      {
        path: 'course/teacher',
        element: <CourseTeacher />,
      },
      {
        path: 'profile',
        element: <Profile />,
      },
    ],
  },
  // 二级页面（无TabBar）
  {
    path: '/practice/answer-sheet',
    element: (
      <Suspense fallback={<PageLoading />}>
        <AnswerSheet />
      </Suspense>
    ),
  },
  {
    path: '/practice/result',
    element: (
      <Suspense fallback={<PageLoading />}>
        <PracticeResult />
      </Suspense>
    ),
  },
  {
    path: '/practice/wrong-analysis',
    element: (
      <Suspense fallback={<PageLoading />}>
        <WrongQuestionAnalysis />
      </Suspense>
    ),
  },
  {
    path: '/practice/history',
    element: (
      <Suspense fallback={<PageLoading />}>
        <HistoryExam />
      </Suspense>
    ),
  },
  {
    path: '/practice/smart',
    element: (
      <Suspense fallback={<PageLoading />}>
        <SmartPaper />
      </Suspense>
    ),
  },
  {
    path: '/practice/timelimit',
    element: (
      <Suspense fallback={<PageLoading />}>
        <TimeLimit />
      </Suspense>
    ),
  },
  {
    path: '/practice/mock',
    element: (
      <Suspense fallback={<PageLoading />}>
        <MockExam />
      </Suspense>
    ),
  },
  {
    path: '/course/live/:id',
    element: (
      <Suspense fallback={<PageLoading />}>
        <LiveDetail />
      </Suspense>
    ),
  },
  {
    path: '/course/video/:id',
    element: (
      <Suspense fallback={<PageLoading />}>
        <VideoDetail />
      </Suspense>
    ),
  },
  {
    path: '/course/teacher/:id',
    element: (
      <Suspense fallback={<PageLoading />}>
        <TeacherDetail />
      </Suspense>
    ),
  },
  // AI工具箱页面
  {
    path: '/ai-tools/diagnose',
    element: (
      <Suspense fallback={<PageLoading />}>
        <AIDiagnose />
      </Suspense>
    ),
  },
  {
    path: '/ai-tools/essay',
    element: (
      <Suspense fallback={<PageLoading />}>
        <EssayCorrection />
      </Suspense>
    ),
  },
  {
    path: '/ai-tools/interview',
    element: (
      <Suspense fallback={<PageLoading />}>
        <InterviewSim />
      </Suspense>
    ),
  },
  {
    path: '/ai-tools/photo',
    element: (
      <Suspense fallback={<PageLoading />}>
        <PhotoQuestion />
      </Suspense>
    ),
  },
  // 教师直播页面
  {
    path: '/teacher/live',
    element: (
      <Suspense fallback={<PageLoading />}>
        <TeacherLive />
      </Suspense>
    ),
  },
  // 个人中心二级页面
  {
    path: '/profile/courses',
    element: (
      <Suspense fallback={<PageLoading />}>
        <MyCourses />
      </Suspense>
    ),
  },
  {
    path: '/profile/wrong-questions',
    element: (
      <Suspense fallback={<PageLoading />}>
        <WrongQuestions />
      </Suspense>
    ),
  },
  {
    path: '/profile/wrong-analysis',
    element: (
      <Suspense fallback={<PageLoading />}>
        <WrongAnalysis />
      </Suspense>
    ),
  },
  {
    path: '/profile/study-report',
    element: (
      <Suspense fallback={<PageLoading />}>
        <StudyReport />
      </Suspense>
    ),
  },
  {
    path: '/profile/favorites',
    element: (
      <Suspense fallback={<PageLoading />}>
        <MyFavorites />
      </Suspense>
    ),
  },
  {
    path: '/profile/notes',
    element: (
      <Suspense fallback={<PageLoading />}>
        <StudyNotes />
      </Suspense>
    ),
  },
  {
    path: '/profile/settings',
    element: (
      <Suspense fallback={<PageLoading />}>
        <Settings />
      </Suspense>
    ),
  },
  {
    path: '/profile/edit',
    element: (
      <Suspense fallback={<PageLoading />}>
        <EditProfile />
      </Suspense>
    ),
  },
  {
    path: '/profile/password',
    element: (
      <Suspense fallback={<PageLoading />}>
        <ChangePassword />
      </Suspense>
    ),
  },
  {
    path: '/profile/phone',
    element: (
      <Suspense fallback={<PageLoading />}>
        <BindPhone />
      </Suspense>
    ),
  },
  {
    path: '/profile/clear-cache',
    element: (
      <Suspense fallback={<PageLoading />}>
        <ClearCache />
      </Suspense>
    ),
  },
  {
    path: '/profile/vip',
    element: (
      <Suspense fallback={<PageLoading />}>
        <VipRecharge />
      </Suspense>
    ),
  },
  // 协议页面
  {
    path: '/agreement/user',
    element: (
      <Suspense fallback={<PageLoading />}>
        <UserAgreement />
      </Suspense>
    ),
  },
  {
    path: '/agreement/privacy',
    element: (
      <Suspense fallback={<PageLoading />}>
        <PrivacyPolicy />
      </Suspense>
    ),
  },
]);

export default router;
